import React, { useCallback, useRef, useState } from "react";
import { Outlet, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  IconButton,
  Toolbar,
  Tooltip,
  CircularProgress,
} from "@mui/material";
import { IconLogout, IconPackages, IconRefresh } from "@tabler/icons-react";
import { clearClientSession, getClientUser } from "../authSession";
import { barIconSx, ds, topBarSx } from "../theme/keeperTheme";

/**
 * Оболочка только для мобильных: верхняя полоса (пользователь + выход), контент.
 */
export default function Layout() {
  const navigate = useNavigate();
  const toolbarRefreshRef = useRef(null);
  const [hasToolbarRefresh, setHasToolbarRefresh] = useState(false);
  const [toolbarRefreshBusy, setToolbarRefreshBusy] = useState(false);

  const registerToolbarRefresh = useCallback((fn) => {
    toolbarRefreshRef.current = typeof fn === "function" ? fn : null;
    setHasToolbarRefresh(typeof fn === "function");
    return () => {
      toolbarRefreshRef.current = null;
      setHasToolbarRefresh(false);
    };
  }, []);

  const handleToolbarRefresh = useCallback(async () => {
    const fn = toolbarRefreshRef.current;
    if (!fn) return;
    setToolbarRefreshBusy(true);
    try {
      await Promise.resolve(fn());
    } finally {
      setToolbarRefreshBusy(false);
    }
  }, []);

  const user = getClientUser();
  const userDisplayName =
    user &&
    [user.first_name, user.last_name].filter(Boolean).join(" ").trim();
  const headerTitle = userDisplayName || user?.email || "Пользователь";
  const headerSubtitle = userDisplayName && user?.email ? user.email : null;

  const handleLogout = () => {
    clearClientSession();
    navigate("/login", { replace: true });
  };

  return (
    <Box
      sx={{
        minHeight: "100dvh",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        bgcolor: ds.bg,
        color: ds.text,
        mt: 8,
      }}
    >
      <Box
        component="header"
        sx={topBarSx}
      >
        <Toolbar
          variant="dense"
          sx={{
            minHeight: 64,
            justifyContent: "space-between",
            px: 1.5,
            gap: 1,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, minWidth: 0, maxWidth: "62%" }}>
            <Box
              sx={{
                width: 38,
                height: 38,
                flexShrink: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: `${ds.radius + 1}px`,
                bgcolor: ds.surface,
                border: "1px solid rgba(37, 99, 235, 0.18)",
                boxShadow: "0 4px 12px rgba(37, 99, 235, 0.18)",
                color: ds.primary,
              }}
            >
              <IconPackages size={21} stroke={1.5} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography noWrap sx={{ fontSize: "0.875rem", fontWeight: 700, color: ds.text, lineHeight: 1.25 }}>
                {headerTitle}
              </Typography>
              {headerSubtitle ? (
                <Typography noWrap sx={{ fontSize: "0.6875rem", fontWeight: 500, color: ds.text2, lineHeight: 1.3 }}>
                  {headerSubtitle}
                </Typography>
              ) : null}
            </Box>
          </Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              minWidth: 0,
            }}
          >
            {hasToolbarRefresh && (
              <Tooltip title="Обновить">
                <span>
                  <IconButton
                    onClick={handleToolbarRefresh}
                    disabled={toolbarRefreshBusy}
                    aria-label="Обновить данные"
                    sx={barIconSx}
                  >
                    {toolbarRefreshBusy ? (
                      <CircularProgress size={20} color="inherit" />
                    ) : (
                      <IconRefresh size={22} stroke={1.35} />
                    )}
                  </IconButton>
                </span>
              </Tooltip>
            )}
            <IconButton onClick={handleLogout} aria-label="Выйти" sx={barIconSx}>
              <IconLogout size={22} stroke={1.35} />
            </IconButton>
          </Box>
        </Toolbar>
      </Box>
      <Box
        component="main"
        sx={{
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <Outlet context={{ registerToolbarRefresh }} />
      </Box>
    </Box>
  );
}
